import { z } from 'zod';
import { IdSchema, SlugSchema, SipDomainSchema, TimestampSchema, ListQuerySchema, MoneySchema } from './common.js';

// Matches the CHECK constraint on tenants.status exactly. 'pending' was never
// a database value -- a tenant starts as 'provisioning'.
export const TenantStatusSchema = z.enum(['provisioning', 'active', 'suspended', 'closed']);
export const BillingModeSchema = z.enum(['prepaid', 'postpaid']);

export const TenantSchema = z.object({
  id: IdSchema,
  reseller_id: IdSchema.nullable(),
  name: z.string(),
  slug: SlugSchema,
  sip_domain: SipDomainSchema,
  /** Where the account's phones send their SIP: one host for every account (26 September 2026). */
  sip_registrar_host: z.string().optional(),
  status: TenantStatusSchema,
  timezone: z.string(),
  country_code: z.string().length(2),
  currency: z.string().length(3),
  billing_mode: BillingModeSchema,
  max_concurrent_calls: z.number().int(),
  max_cps: z.number().int(),
  max_extensions: z.number().int().optional(),
  call_plan_id: IdSchema.nullable().optional(),
  /** 'arcnet': calls use our trunks. 'byot': the customer's own (0044). */
  trunk_mode: z.enum(['arcnet', 'byot']).optional(),
  /** Why the account is suspended or closed, while it is. */
  suspend_reason: z.string().nullable().optional(),
  /** The switch that serves the account, and its name. Null: on none, so nothing reaches a switch. */
  primary_node_id: IdSchema.nullable().optional(),
  primary_node_name: z.string().nullable().optional(),
  /** The account's monthly spend cap (0132); null for none. */
  spend_cap_monthly: z.string().nullable().optional(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
});
export type Tenant = z.infer<typeof TenantSchema>;

/**
 * sip_domain is absent by design. It is permanent once set -- a tenant's phones
 * are configured with it, so changing it de-registers every handset. The
 * database enforces this with a trigger (arc.forbid_sip_domain_change); leaving
 * it out of the update DTO means the API never even offers the mistake.
 */
export const TenantUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  timezone: z.string().min(1).max(64).optional(),
  country_code: z.string().length(2).optional(),
  billing_mode: BillingModeSchema.optional(),
  max_concurrent_calls: z.number().int().min(0).max(10000).optional(),
  max_cps: z.number().int().min(0).max(1000).optional(),
  /**
   * Which switch serves this account.
   *
   * Not cosmetic: a node only receives the configuration for tenants homed to
   * it, so an unhomed tenant can register handsets and place calls (the node
   * read model was seeded by hand) while every synced feature -- dial codes,
   * and everything that follows them -- silently does nothing for it.
   */
  primary_node_id: IdSchema.nullable().optional(),
  standby_node_id: IdSchema.nullable().optional(),
  /** Outside calls stop when the month's spend reaches it (0132). Null removes it. */
  spend_cap_monthly: MoneySchema.nullable().optional(),
  /** Seats this account may create. Enforced when an extension is made (0003). */
  max_extensions: z.number().int().min(1).max(100000).optional(),
}).strict();
export type TenantUpdate = z.infer<typeof TenantUpdateSchema>;

/**
 * Slugs nobody may take, because they would collide with a platform hostname.
 *
 * ONE list. It was written out twice -- tenancy/routes.ts and resellers.ts --
 * with identical contents and no link between them, which is a duplication that
 * only shows up the day somebody adds a hostname to one and not the other. Both
 * a tenant slug and a reseller slug become a hostname label, so both need the
 * same list.
 *
 * A constant rather than a settings table, deliberately. Reserving a name is
 * not an operational decision somebody revisits; it happens because a hostname
 * was added, which is a deploy. A table here would mean a database round trip
 * on every account creation to read a list that changes once a year.
 */
export const RESERVED_SLUGS: readonly string[] = [
  'api', 'portal', 'prov', 'admin', 'www', 'status', 'app', 'sip', 'ns', 'mail',
];

export const TenantCreateSchema = z.object({
  name: z.string().min(1).max(200),
  slug: SlugSchema,
  sip_domain: SipDomainSchema,
  reseller_id: IdSchema.optional(),
  timezone: z.string().min(1).max(64).default('Africa/Johannesburg'),
  country_code: z.string().length(2).default('ZA'),
  currency: z.string().length(3).default('ZAR'),
  billing_mode: BillingModeSchema.default('prepaid'),
  max_concurrent_calls: z.number().int().min(0).max(10000).default(20),
  max_cps: z.number().int().min(0).max(1000).default(5),
}).strict();
export type TenantCreate = z.infer<typeof TenantCreateSchema>;

export const TenantListQuerySchema = ListQuerySchema.extend({
  status: TenantStatusSchema.optional(),
  reseller_id: IdSchema.optional(),
  q: z.string().max(200).optional(),
  sort: z.enum(['name', '-name', 'created_at', '-created_at']).default('-created_at'),
});

export const SiteSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  timezone: z.string().nullable(),
  address: z.string().nullable(),
  created_at: TimestampSchema,
});
export type Site = z.infer<typeof SiteSchema>;

// ------------------------------------------------------------- resellers
//
// The schema this replaced was never checked against the table: it declared a
// `currency` column resellers does not have, omitted branding, portal_domain,
// updated_at and version, and gave status two values where the CHECK
// constraint allows three -- so the first closed reseller to be returned would
// have failed to parse. Nothing consumed it, which is why it survived.

export const ResellerStatusSchema = z.enum(['active', 'suspended', 'closed']);

/**
 * A reseller: a partner who owns customers on this platform.
 *
 * `branding` and `portal_domain` are the white-label pair -- a reseller's
 * customers reach the portal on the reseller's own hostname and see the
 * reseller's name and colours, not ours. Both are nullable/empty because a
 * reseller is useful before either is arranged.
 *
 * Counts are joined on, not stored: a reseller's tenant list is derived from
 * tenants.reseller_id, and a denormalised count is a number that goes stale the
 * first time a customer is moved.
 */
export const ResellerSchema = z.object({
  id: IdSchema,
  slug: SlugSchema,
  name: z.string(),
  status: ResellerStatusSchema,
  branding: z.record(z.string(), z.unknown()),
  portal_domain: z.string().nullable(),
  tenant_count: z.number().int(),
  /** Customers on this reseller that are not closed -- what they actually run. */
  active_tenant_count: z.number().int(),
  user_count: z.number().int(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
});
export type Reseller = z.infer<typeof ResellerSchema>;

export const ResellerCreateSchema = z.object({
  name: z.string().min(1).max(200),
  slug: SlugSchema,
  /**
   * The hostname their customers use. Unique across the platform, because two
   * resellers claiming one domain is a routing question with no answer.
   */
  portal_domain: z.string().min(3).max(253).nullable().optional(),
  branding: z.record(z.string(), z.unknown()).optional(),
}).strict();
export type ResellerCreate = z.infer<typeof ResellerCreateSchema>;

/**
 * `slug` is absent by design, matching TenantUpdateSchema's treatment of
 * sip_domain. It is the reseller's identifier in URLs and in the seeded product
 * keys scoped to them (extension_types and call_plans are unique on
 * (reseller_id, key)); renaming it silently orphans anything that recorded it.
 */
export const ResellerUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  status: ResellerStatusSchema.optional(),
  portal_domain: z.string().min(3).max(253).nullable().optional(),
  branding: z.record(z.string(), z.unknown()).optional(),
}).strict();
export type ResellerUpdate = z.infer<typeof ResellerUpdateSchema>;

export const ResellerListQuerySchema = ListQuerySchema.extend({
  status: ResellerStatusSchema.optional(),
  search: z.string().max(120).optional(),
});
